import React, { forwardRef, type ReactNode, type MouseEvent } from "react";

import { Link } from "react-router-dom";
import { cn } from "src/shadcn/lib/utils";
import { renderIcon, type IconProp } from "src/shared/renderIcon";
import { Button as PrimitiveButton } from "src/primitives/Button";
import { Spinner } from "src/primitives/Spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/primitives/Tooltip";

import {
  ICON_ONLY_SIZE_MAP,
  SPINNER_SIZE_MAP,
  TOOLTIP_SIDE_MAP,
} from "./constants";

export const BUTTON_VARIANTS = [
  "default",
  "secondary",
  "destructive",
  "destructive-soft",
  "outline",
  "ghost",
  "link",
] as const;

export const BUTTON_SIZES = [
  "default",
  "xs",
  "sm",
  "lg",
  "icon",
  "icon-xs",
  "icon-sm",
  "icon-lg",
] as const;

export type ButtonVariant = (typeof BUTTON_VARIANTS)[number];
export type ButtonSize = (typeof BUTTON_SIZES)[number];

type IconPosition = "left" | "right";

type ButtonType = "button" | "reset" | "submit";

interface ButtonTooltipProps {
  content?: ReactNode;
  position?: string;
  disabled?: boolean;
}

export interface ButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "type" | "disabled" | "className" | "children" | "onClick"
> {
  /** Visual variant of the button. */
  variant?: ButtonVariant;
  /** Size of the button. */
  size?: ButtonSize;
  /** Text label rendered inside the button. */
  label?: string;
  /** Icon component, element, or class-name string. */
  icon?: IconProp;
  /** Placement of the icon relative to the label. */
  iconPosition?: IconPosition;
  /** Show a loading spinner and prevent clicks. */
  loading?: boolean;
  /** Disable the button. */
  disabled?: boolean;
  /** Click handler. */
  onClick?: (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  /** Internal route (react-router `Link`). */
  to?: string;
  /** External URL (renders an anchor tag). */
  href?: string;
  /** HTML button type attribute. */
  type?: ButtonType;
  /** Stretch the button to fill its container. */
  fullWidth?: boolean;
  /** Additional CSS class names. */
  className?: string;
  /** Props forwarded to a wrapping Tooltip. */
  tooltipProps?: ButtonTooltipProps | null;
  /** Render as a custom element (polymorphic). */
  asChild?: boolean;
  /**
   * Content rendered after the label and any right-side icon (e.g., a trailing
   * chevron, badge, or shortcut indicator). Auto-wrapped with
   * `data-icon="inline-end"` so the button's compact end-padding kicks in.
   */
  trailing?: ReactNode;
  /** Children rendered inside the button (fallback for `label`). */
  children?: ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "default",
      size = "default",
      label,
      icon = null,
      iconPosition = "right",
      loading = false,
      disabled = false,
      onClick,
      to,
      href,
      type = "button",
      fullWidth = false,
      className,
      tooltipProps = null,
      asChild = false,
      trailing,
      children,
      ...otherProps
    },
    ref
  ) => {
    const isIconSize = size.startsWith("icon");
    const renderLabel = isIconSize ? null : label || children;
    const isIconOnly = !renderLabel && !!icon;
    const resolvedSize = isIconOnly ? (ICON_ONLY_SIZE_MAP[size] ?? size) : size;

    // --- Loading / disabled state ---
    const isDisabled = disabled || loading;

    // --- Click handler ---
    const handleClick = (
      e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>
    ) => {
      if (loading || disabled) {
        e.preventDefault();
        return;
      }
      onClick?.(e);
    };

    // --- Icon elements ---
    const iconElement = icon ? renderIcon(icon) : null;

    // --- Build the inner content ---
    const buttonContent = (
      <>
        {loading && (
          <span className="absolute inset-0 flex items-center justify-center">
            <Spinner
              aria-hidden="true"
              className={SPINNER_SIZE_MAP[resolvedSize]}
            />
          </span>
        )}
        <span
          className={cn(
            "inline-flex items-center gap-1.5",
            loading && "invisible"
          )}
        >
          {iconPosition === "left" && iconElement && (
            <span data-icon="inline-start">{iconElement}</span>
          )}
          {renderLabel && <span>{renderLabel}</span>}
          {iconPosition === "right" && iconElement && (
            <span data-icon="inline-end">{iconElement}</span>
          )}
          {trailing && <span data-icon="inline-end">{trailing}</span>}
        </span>
      </>
    );

    // --- Merged className ---
    const mergedClassName = cn(
      fullWidth && "w-full",
      loading && "relative pointer-events-none",
      variant === "link" && "[table_&]:max-w-full [table_&]:whitespace-normal",
      className
    );

    // --- Determine render element ---
    let buttonElement: ReactNode;

    if (asChild) {
      buttonElement = (
        <PrimitiveButton
          ref={ref}
          asChild
          variant={variant}
          size={resolvedSize}
          className={mergedClassName}
          disabled={isDisabled}
          {...otherProps}
        >
          {children}
        </PrimitiveButton>
      );
    } else if (!disabled && to) {
      buttonElement = (
        <PrimitiveButton
          asChild
          variant={variant}
          size={resolvedSize}
          className={mergedClassName}
          disabled={isDisabled}
          {...otherProps}
        >
          <Link
            innerRef={ref as React.Ref<HTMLAnchorElement>}
            to={to}
            onClick={handleClick}
          >
            {buttonContent}
          </Link>
        </PrimitiveButton>
      );
    } else if (!disabled && href) {
      buttonElement = (
        <PrimitiveButton
          asChild
          variant={variant}
          size={resolvedSize}
          className={mergedClassName}
          disabled={isDisabled}
          {...otherProps}
        >
          <a
            ref={ref as React.Ref<HTMLAnchorElement>}
            href={href}
            onClick={handleClick}
          >
            {buttonContent}
          </a>
        </PrimitiveButton>
      );
    } else {
      buttonElement = (
        <PrimitiveButton
          ref={ref}
          type={type}
          variant={variant}
          size={resolvedSize}
          className={mergedClassName}
          disabled={isDisabled}
          onClick={handleClick}
          {...otherProps}
        >
          {buttonContent}
        </PrimitiveButton>
      );
    }

    // --- Tooltip wrapper ---
    if (tooltipProps) {
      const { content, position, disabled: tooltipDisabled } = tooltipProps;

      if (tooltipDisabled || !content) {
        return <>{buttonElement}</>;
      }

      const side = position ? TOOLTIP_SIDE_MAP[position] : undefined;

      // Wrap disabled buttons in a span so tooltip still triggers
      const triggerContent = isDisabled ? (
        <span className="inline-flex">{buttonElement}</span>
      ) : (
        buttonElement
      );

      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{triggerContent}</TooltipTrigger>
            <TooltipContent side={side}>{content}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return <>{buttonElement}</>;
  }
);

Button.displayName = "Button";

export { Button };
